import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Megaphone, Pencil, Pin, Trash2 } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pagination } from '@/components/members/pagination'
import { useAnnouncements, useDeleteAnnouncement } from '@/hooks/use-announcements'
import { formatDate } from '@/lib/utils'
import { AnnouncementModal } from './announcement-modal'
import { htmlToExcerpt } from '@riverpark/utils/html'
import type { Announcement, AnnouncementCategory } from '@riverpark/types'

const PAGE_SIZE = 20

const CATEGORY_BADGE: Record<
  AnnouncementCategory,
  { variant: 'overdue' | 'pending' | 'credit' | 'committee' | 'default'; label: string }
> = {
  urgent: { variant: 'overdue', label: 'Urgent' },
  maintenance: { variant: 'pending', label: 'Maintenance' },
  financial: { variant: 'credit', label: 'Financial' },
  governance: { variant: 'committee', label: 'Governance' },
  general: { variant: 'default', label: 'General' },
}

export function AnnouncementsWorkspace({ basePath }: { basePath: string }) {
  const [page, setPage] = useState(1)
  const [composing, setComposing] = useState(false)
  const [editing, setEditing] = useState<Announcement | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Announcement | null>(null)

  const { data, isLoading } = useAnnouncements({ page, limit: PAGE_SIZE })
  const deleteAnnouncement = useDeleteAnnouncement()
  const items = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }

  async function handleDelete() {
    if (!confirmDelete) return
    await deleteAnnouncement.mutateAsync(confirmDelete.id)
    setConfirmDelete(null)
  }

  return (
    <PageShell>
      <PageHeader
        label="Content"
        title="Announcements"
        description="Post notices to the estate notice board — optionally as an SMS broadcast too."
        action={
          <Button size="sm" onClick={() => setComposing(true)}>
            <Megaphone size={14} className="mr-1.5" />
            Post announcement
          </Button>
        }
      />

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No announcements yet.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-card border border-border-2 bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  {a.pinned && <Pin size={14} className="text-primary" />}
                  <Link
                    to={`${basePath}/${a.id}`}
                    className="font-serif text-lg text-ink hover:text-primary hover:underline"
                  >
                    {a.title}
                  </Link>
                  <Badge variant={CATEGORY_BADGE[a.category].variant}>
                    {CATEGORY_BADGE[a.category].label}
                  </Badge>
                  {a.status === 'draft' && <Badge variant="draft">Draft</Badge>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-cream-2 hover:text-ink"
                    aria-label="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(a)}
                    className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-cream-2 hover:text-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="mt-2 font-sans text-sm text-ink">{htmlToExcerpt(a.body)}</p>
              <p className="mt-2 font-sans text-xs text-muted-foreground">
                {a.authorName} · {formatDate(new Date(a.createdAt * 1000))}
                {a.smsSent && ' · SMS sent'}
              </p>
            </div>
          ))}
        </div>
      )}

      {meta.total > 0 && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          emptyLabel="No announcements"
          className="rounded border border-border-2 bg-card"
        />
      )}

      {composing && <AnnouncementModal onClose={() => setComposing(false)} />}
      {editing && <AnnouncementModal announcement={editing} onClose={() => setEditing(null)} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/40 p-4">
          <div className="w-full max-w-sm rounded-card border border-border-2 bg-card p-6">
            <h2 className="font-serif text-lg text-ink">Delete announcement?</h2>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              "{confirmDelete.title}" will be removed from the feed. This can't be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={deleteAnnouncement.isPending}
                onClick={() => void handleDelete()}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  )
}
